import { useEffect, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { API_URL } from "@/config";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ArrowLeft, Upload, Trash2, Loader2, Image } from "lucide-react";

const http = axios.create({ baseURL: API_URL, withCredentials: true });

type Tab = "details" | "floorplan" | "photos";

interface Vehicle {
    vid: number;
    did: number;
    vyear: string | null;
    vmake: string | null;
    vmodel: string | null;
    vtrim: string | null;
    vvin: string | null;
    vcondition: string | null;
    vmileage: string | null;
    vecolor_txt: string | null;
    vicolor_txt: string | null;
    vbody: string | null;
    vtransm: string | null;
    vengine: string | null;
    vfueltype: string | null;
    vdrivetrain: string | null;
    vcylinders: string | null;
    vdoors: string | null;
    vrprice: string | null;
    vdprice: string | null;
    vspecialprice: string | null;
    vstockno: string | null;
    vlivestatus: string | null;
    vfeatured: boolean | null;
    vcomments: string | null;
    vPhotoURLs: string | null;
}

interface FloorPlan {
    floorplan_company_name: string | null;
    credit_limit: string | null;
    available_credit: string | null;
}

interface Photo { v_photoid: number; photo_file_name: string; photo_file_path: string; }

function Field({ label, children }: { label: string; children: React.ReactNode }) {
    return (
        <div>
            <label className="text-xs font-medium uppercase text-gray-400 mb-1 block">{label}</label>
            {children}
        </div>
    );
}

export default function DealerInventoryEditPage() {
    const { vid } = useParams<{ vid: string }>();
    const navigate = useNavigate();
    const [tab, setTab] = useState<Tab>("details");
    const [vehicle, setVehicle] = useState<Vehicle | null>(null);
    const [form, setForm] = useState<Partial<Vehicle>>({});
    const [saving, setSaving] = useState(false);
    const [saved, setSaved] = useState(false);
    const [floorplan, setFloorplan] = useState<FloorPlan | null>(null);
    const [photos, setPhotos] = useState<Photo[]>([]);
    const [urlPhotos, setUrlPhotos] = useState<string[]>([]);
    const [uploading, setUploading] = useState(false);
    const fileRef = useRef<HTMLInputElement>(null);

    useEffect(() => {
        if (!vid) return;
        http.get<Vehicle>(`/vehicles/vid/${vid}`)
            .then(r => {
                setVehicle(r.data);
                setForm(r.data);
                try { setUrlPhotos(JSON.parse(r.data.vPhotoURLs ?? "[]")); } catch { /* ignore */ }
            })
            .catch(() => {});
    }, [vid]);

    // Load floor plan when tab opens
    useEffect(() => {
        if (tab !== "floorplan" || !vehicle?.did) return;
        http.get<any>(`/dealers/${vehicle.did}`)
            .then(r => {
                const fp = r.data?.dealer_floorplan_link?.[0];
                if (fp) {
                    setFloorplan({
                        floorplan_company_name: fp.floor_plan_company?.floorplan_company_name ?? null,
                        credit_limit: fp.credit_limit ?? null,
                        available_credit: fp.available_credit ?? null,
                    });
                }
            })
            .catch(() => {});
    }, [tab, vehicle?.did]);

    // Load photos when tab opens
    useEffect(() => {
        if (tab !== "photos" || !vehicle?.vvin) return;
        http.get<Photo[]>(`/vehicles/vin/${vehicle.vvin}/photos`)
            .then(r => setPhotos(r.data))
            .catch(() => {});
    }, [tab, vehicle?.vvin]);

    const set = (field: keyof Vehicle, value: unknown) => setForm(p => ({ ...p, [field]: value }));

    const save = async () => {
        if (!vid) return;
        setSaving(true);
        try {
            await http.put(`/vehicles/${vid}`, form);
            setSaved(true);
            setTimeout(() => setSaved(false), 3000);
        } finally {
            setSaving(false);
        }
    };

    const uploadPhotos = async (files: FileList) => {
        if (!vid || !files.length) return;
        setUploading(true);
        const fd = new FormData();
        Array.from(files).forEach(f => fd.append("photos", f));
        try {
            const res = await http.post<{ urls: string[] }>(`/vehicles/${vid}/photos`, fd, {
                headers: { "Content-Type": "multipart/form-data" },
            });
            setUrlPhotos(prev => [...prev, ...res.data.urls]);
        } finally {
            setUploading(false);
        }
    };

    const deletePhoto = async (photoId: number) => {
        if (!vid) return;
        await http.delete(`/vehicles/${vid}/photos/${photoId}`);
        setPhotos(prev => prev.filter(p => p.v_photoid !== photoId));
    };

    if (!vehicle) {
        return (
            <div className="flex items-center justify-center py-24">
                <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
            </div>
        );
    }

    const tabs: Tab[] = ["details", "floorplan", "photos"];

    return (
        <div className="p-6 max-w-3xl mx-auto space-y-6">
            <div className="flex items-center gap-3">
                <button onClick={() => navigate("/dealer/inventory")} className="text-gray-400 hover:text-gray-600">
                    <ArrowLeft className="h-5 w-5" />
                </button>
                <h1 className="text-xl font-light uppercase tracking-wide">
                    {[vehicle.vyear, vehicle.vmake, vehicle.vmodel].filter(Boolean).join(" ") || `Vehicle #${vid}`}
                </h1>
            </div>

            {/* Tabs */}
            <div className="flex gap-4 border-b">
                {tabs.map(t => (
                    <button
                        key={t}
                        onClick={() => setTab(t)}
                        className={`pb-2 text-sm font-medium capitalize border-b-2 transition ${tab === t ? "border-indigo-600 text-indigo-600" : "border-transparent text-gray-500"}`}
                    >
                        {t}
                    </button>
                ))}
            </div>

            {/* Details tab */}
            {tab === "details" && (
                <div className="bg-white rounded-2xl shadow p-6 space-y-6">
                    <div className="grid grid-cols-2 gap-4">
                        <Field label="Year">
                            <Input value={form.vyear ?? ""} onChange={e => set("vyear", e.target.value)} />
                        </Field>
                        <Field label="Make">
                            <Input value={form.vmake ?? ""} onChange={e => set("vmake", e.target.value)} />
                        </Field>
                        <Field label="Model">
                            <Input value={form.vmodel ?? ""} onChange={e => set("vmodel", e.target.value)} />
                        </Field>
                        <Field label="Trim">
                            <Input value={form.vtrim ?? ""} onChange={e => set("vtrim", e.target.value)} />
                        </Field>
                        <Field label="VIN">
                            <Input value={form.vvin ?? ""} onChange={e => set("vvin", e.target.value)} />
                        </Field>
                        <Field label="Stock #">
                            <Input value={form.vstockno ?? ""} onChange={e => set("vstockno", e.target.value)} />
                        </Field>
                        <Field label="Condition">
                            <select
                                className="w-full border rounded-lg px-3 py-2 text-sm"
                                value={form.vcondition ?? ""}
                                onChange={e => set("vcondition", e.target.value)}
                            >
                                <option value="">— Select —</option>
                                <option value="NEW">New</option>
                                <option value="USED">Used</option>
                                <option value="CPO">Certified Pre-Owned</option>
                            </select>
                        </Field>
                        <Field label="Mileage">
                            <Input type="number" value={form.vmileage ?? ""} onChange={e => set("vmileage", e.target.value)} />
                        </Field>
                        <Field label="Body Type">
                            <Input value={form.vbody ?? ""} onChange={e => set("vbody", e.target.value)} />
                        </Field>
                        <Field label="Transmission">
                            <Input value={form.vtransm ?? ""} onChange={e => set("vtransm", e.target.value)} />
                        </Field>
                        <Field label="Engine">
                            <Input value={form.vengine ?? ""} onChange={e => set("vengine", e.target.value)} />
                        </Field>
                        <Field label="Drivetrain">
                            <Input value={form.vdrivetrain ?? ""} onChange={e => set("vdrivetrain", e.target.value)} />
                        </Field>
                        <Field label="Exterior Color">
                            <Input value={form.vecolor_txt ?? ""} onChange={e => set("vecolor_txt", e.target.value)} />
                        </Field>
                        <Field label="Interior Color">
                            <Input value={form.vicolor_txt ?? ""} onChange={e => set("vicolor_txt", e.target.value)} />
                        </Field>
                        <Field label="Retail Price ($)">
                            <Input type="number" value={form.vrprice ?? ""} onChange={e => set("vrprice", e.target.value)} />
                        </Field>
                        <Field label="Dealer Price ($)">
                            <Input type="number" value={form.vdprice ?? ""} onChange={e => set("vdprice", e.target.value)} />
                        </Field>
                        <Field label="Special Price ($)">
                            <Input type="number" value={form.vspecialprice ?? ""} onChange={e => set("vspecialprice", e.target.value)} />
                        </Field>
                        <Field label="Status">
                            <select
                                className="w-full border rounded-lg px-3 py-2 text-sm"
                                value={form.vlivestatus ?? "1"}
                                onChange={e => set("vlivestatus", e.target.value)}
                            >
                                <option value="1">Live</option>
                                <option value="0">Off / Hold</option>
                            </select>
                        </Field>
                    </div>

                    <Field label="Featured">
                        <label className="flex items-center gap-2 cursor-pointer">
                            <input
                                type="checkbox"
                                checked={!!form.vfeatured}
                                onChange={e => set("vfeatured", e.target.checked)}
                                className="rounded"
                            />
                            <span className="text-sm">Show as featured vehicle</span>
                        </label>
                    </Field>

                    <Field label="Comments / Description">
                        <textarea
                            className="w-full border rounded-lg px-3 py-2 text-sm resize-none"
                            rows={4}
                            value={form.vcomments ?? ""}
                            onChange={e => set("vcomments", e.target.value)}
                        />
                    </Field>

                    <div className="flex items-center gap-3">
                        <Button
                            onClick={save}
                            disabled={saving}
                            className="bg-indigo-600 hover:bg-indigo-700 text-white gap-2"
                        >
                            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                            {saving ? "Saving..." : "Save Changes"}
                        </Button>
                        {saved && <span className="text-sm text-green-600">Saved!</span>}
                    </div>
                </div>
            )}

            {/* Floor Plan tab */}
            {tab === "floorplan" && (
                <div className="bg-white rounded-2xl shadow p-6 space-y-4">
                    <h2 className="font-semibold">Floor Plan Information</h2>
                    {floorplan ? (
                        <div className="grid grid-cols-2 gap-4 text-sm">
                            <div>
                                <p className="text-xs text-gray-400 uppercase">Floor Plan Company</p>
                                <p className="font-medium">{floorplan.floorplan_company_name ?? "—"}</p>
                            </div>
                            <div>
                                <p className="text-xs text-gray-400 uppercase">Credit Limit</p>
                                <p className="font-medium">{floorplan.credit_limit ? `$${Number(floorplan.credit_limit).toLocaleString()}` : "—"}</p>
                            </div>
                            <div>
                                <p className="text-xs text-gray-400 uppercase">Available Credit</p>
                                <p className="font-medium">{floorplan.available_credit ? `$${Number(floorplan.available_credit).toLocaleString()}` : "—"}</p>
                            </div>
                        </div>
                    ) : (
                        <p className="text-sm text-gray-400">No floor plan linked to this dealership.</p>
                    )}
                </div>
            )}

            {/* Photos tab */}
            {tab === "photos" && (
                <div className="bg-white rounded-2xl shadow p-6 space-y-4">
                    <div className="flex items-center justify-between">
                        <h2 className="font-semibold">Vehicle Photos</h2>
                        <Button
                            onClick={() => fileRef.current?.click()}
                            disabled={uploading}
                            className="gap-2 bg-indigo-600 hover:bg-indigo-700 text-white"
                        >
                            {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
                            Upload
                        </Button>
                        <input
                            ref={fileRef}
                            type="file"
                            accept=".jpg,.jpeg,.png,.webp,.heic"
                            multiple
                            className="hidden"
                            onChange={e => e.target.files && uploadPhotos(e.target.files)}
                        />
                    </div>

                    {/* Uploaded from DB (vehicle_photos table) */}
                    {photos.length > 0 && (
                        <div>
                            <p className="text-xs text-gray-400 uppercase mb-2">Uploaded Photos</p>
                            <div className="grid grid-cols-3 gap-3">
                                {photos.map(p => (
                                    <div key={p.v_photoid} className="relative group">
                                        <div className="h-28 bg-gray-100 rounded-xl overflow-hidden">
                                            <img src={p.photo_file_path} alt="" className="w-full h-full object-cover" />
                                        </div>
                                        <button
                                            onClick={() => deletePhoto(p.v_photoid)}
                                            className="absolute top-1 right-1 p-1 bg-red-500 text-white rounded-lg opacity-0 group-hover:opacity-100 transition"
                                        >
                                            <Trash2 className="h-3 w-3" />
                                        </button>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* URL-based photos (vPhotoURLs JSON) */}
                    {urlPhotos.length > 0 && (
                        <div>
                            <p className="text-xs text-gray-400 uppercase mb-2">Photo URLs</p>
                            <div className="grid grid-cols-3 gap-3">
                                {urlPhotos.map((url, i) => (
                                    <div key={i} className="h-28 bg-gray-100 rounded-xl overflow-hidden">
                                        <img src={url} alt="" className="w-full h-full object-cover" />
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    {photos.length === 0 && urlPhotos.length === 0 && (
                        <div className="text-center py-12">
                            <Image className="h-12 w-12 mx-auto text-gray-300 mb-3" />
                            <p className="text-sm text-gray-400">No photos yet. Upload some above.</p>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}
